import { Component, computed, inject, linkedSignal, signal } from "@angular/core"
import { NgClass } from "@angular/common"
import { httpResource } from "@angular/common/http"
import { ActivatedRoute, Router } from "@angular/router"
import { toSignal } from "@angular/core/rxjs-interop"
import { catchError, firstValueFrom, map, of, take } from "rxjs"
import { MatDialog } from "@angular/material/dialog"
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner"
import { FormField, form, submit } from "@angular/forms/signals"
import {
    EmployeeMbrad,
    ModifyObjectFactory,
    ModifySurvey,
    ModifySurveyFactory,
    SURVEY_FIELD_LABELS,
    Survey,
} from "../../models"
import { EmployeeService, SurveyService, ToastService } from "../../services"
import { JsonPatchDocumentGenerator } from "../../helpers/JsonPatchDocumentGenerator"
import { FiltersComponent } from "../../components/filters/filters.component"
import { DateRangePickerComponent } from "../../components/date-range-picker/date-range-picker.component"
import { FiltersValue, emptyFilters, fromDependencies, toDependencies } from "../../helpers/DependenciesForm"
import { BooleanInputComponent } from "../../components/boolean-input/boolean-input.component"
import { TextInputComponent } from "../../components/text-input/text-input.component"
import { FileInputComponent } from "../../components/file-input/file-input.component"
import {
    ConfirmDialogComponent,
    DictionaryInputComponent,
} from "../../components"
import {
    PATCH_IGNORED_FIELDS,
    describeError,
    invalidFieldsDetail,
    toServerFieldErrors,
} from "../../helpers/FormErrors"

@Component({
    selector: "valben-surveys-edit",
    templateUrl: "./surveys-edit.page.html",
    styles: [`
        :host {
            width: 100%;
            flex: 1 1 auto;
            min-height: 0;
            display: flex;
            flex-direction: column;
        }
    `],
    standalone: true,
    imports: [
        NgClass,
        MatProgressSpinnerModule,
        FormField,
        FiltersComponent,
        DictionaryInputComponent,
        BooleanInputComponent,
        FileInputComponent,
        TextInputComponent,
        DateRangePickerComponent,
    ],
})
export class SurveysEditPage {
    private _surveyService = inject(SurveyService)
    private _messageService = inject(ToastService)
    private _router = inject(Router)
    private _dialog = inject(MatDialog)
    private _patchGenerator = new JsonPatchDocumentGenerator()

    private _masterId = toSignal(inject(ActivatedRoute).paramMap.pipe(map(p => p.get("masterId") ?? "")), {
        initialValue: "",
    })

    survey = httpResource<Survey>(() => {
        const masterId = this._masterId()
        return masterId ? this._surveyService.ByMasterIdUrl(masterId) : undefined
    })

    private _employees = toSignal(
        inject(EmployeeService).employees$.pipe(
            take(1),
            catchError(() => of([] as EmployeeMbrad[]))
        )
    )

    employeesReady = computed(() => this._employees() !== undefined)

    saving = signal(false)
    tomorrow = new Date(new Date().setHours(24, 0, 0, 0))

    model = linkedSignal<ModifySurvey>(() => {
        const survey = this.survey.value()
        return survey ? ModifySurveyFactory.from(survey) : ModifySurveyFactory.create()
    })

    surveyForm = form(this.model)
    filters = linkedSignal<FiltersValue>(() => {
        const survey = this.survey.value()
        const employees = this._employees()
        return survey ? fromDependencies(survey.dependencies, employees ?? []) : emptyFilters()
    })

    loading = computed(() => this.survey.isLoading() || this.saving())
    loaded = computed(() => this.survey.hasValue())

    async save(): Promise<void> {
        const original = this.survey.value()
        if (!original) return

        this.surveyForm.dependencies().value.set(toDependencies(this.filters(), original.dependencies))
        this.saving.set(true)
        let failed = false

        const saved = await submit(this.surveyForm, {
            action: async f => {
                const patch = this._patchGenerator.getJsonPatchDocument(
                    ModifySurveyFactory.from(original),
                    f().value(),
                    PATCH_IGNORED_FIELDS
                )

                try {
                    await firstValueFrom(
                        this._surveyService.Modify(
                            this._masterId(),
                            ModifyObjectFactory.create(patch, original.version)
                        )
                    )
                    return null
                } catch (err) {
                    failed = true
                    const { detail, errors } = toServerFieldErrors(err, this.surveyForm, SURVEY_FIELD_LABELS)
                    this._error(detail)
                    return errors
                }
            },
            onInvalid: () => this._error(invalidFieldsDetail(this.surveyForm, SURVEY_FIELD_LABELS)),
        })

        this.saving.set(false)
        if (failed || !saved) return

        this._messageService.add({
            severity: "success",
            summary: "Success",
            detail: "Uspješno izmijenjeno",
            life: 10000,
        })
        this.back()
    }

    async delete(): Promise<void> {
        this.saving.set(true)
        try {
            await firstValueFrom(this._surveyService.Delete(this._masterId()))
            this._messageService.add({
                severity: "success",
                summary: "Success",
                detail: "Uspješno obrisano",
                life: 10000,
            })
            this.back()
        } catch (err) {
            this._error(describeError(err).message)
        } finally {
            this.saving.set(false)
        }
    }

    async showDeleteModal(): Promise<void> {
        const confirmed = await firstValueFrom(
            this._dialog
                .open(ConfirmDialogComponent, {
                    width: "35rem",
                    data: { header: "Jeste li sigurni da želite obrisati entitet?" },
                })
                .afterClosed()
        )
        if (confirmed) await this.delete()
    }

    back(): void {
        this._router.navigate(["/surveys"])
    }

    private _error(detail: string): void {
        this._messageService.add({ severity: "error", summary: "Error", detail, life: 10000 })
    }
}
